import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Grid,
  Button,
  Fab,
  Stack,
} from '@mui/material';
import { Search, Add, Notifications } from '@mui/icons-material';
import { DashedPanel } from './DashedPanel.styles';
import { VisibleScrollbarArea } from './VisibleScrollbarArea.styles';

import { useAlertsViewController } from '../../controllers';
import { AlertEditFormData, Alert } from '../../types';
import { AlertCard } from '../AlertCard/AlertCard';
import { AlertDrawer } from '../AlertDrawer/AlertDrawer';

export const AlertsView: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [hasMoreAlerts, setHasMoreAlerts] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const {
    isPageLoading,
    isSearching,
    isCreating,
    isUpdating,
    alertsList,
    loadMoreAlerts,
    changeAlertsSearchFilter,
    createAlertData,
    updateAlertData,
    deleteAlertData,
  } = useAlertsViewController();

  const alertsToDisplay = useMemo((): Alert[] => {
    return alertsList?.items || [];
  }, [alertsList]);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);
    changeAlertsSearchFilter(event, query);
  }, [changeAlertsSearchFilter]);

  const fetchMoreAlerts = useCallback(() => {
    if (isLoadingMore || !hasMoreAlerts || isPageLoading || isSearching) {
      return;
    }

    setIsLoadingMore(true);
    loadMoreAlerts()
      .then(() => {
        if (alertsList) {
          const hasMore = alertsList.count > alertsList.items.length;
          setHasMoreAlerts(hasMore);
        }
      })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        setIsLoadingMore(false);
      });
  }, [isLoadingMore, hasMoreAlerts, isPageLoading, isSearching, loadMoreAlerts, alertsList]);

  // Scroll detection for infinite scroll
  const onScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      const isNearBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 50;
      
      if (isNearBottom && hasMoreAlerts && !isLoadingMore && !isPageLoading && !isSearching) {
        fetchMoreAlerts();
      }
    }
  }, [hasMoreAlerts, isLoadingMore, isPageLoading, isSearching, fetchMoreAlerts]);

  // Set up scroll listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', onScroll);
      return () => {
        container.removeEventListener('scroll', onScroll);
      };
    }
  }, [onScroll]);

  // Update hasMore when data changes
  useEffect(() => {
    if (alertsList) {
      const hasMore = alertsList.count > alertsList.items.length;
      setHasMoreAlerts(hasMore);
    }
  }, [alertsList]);

  const handleSaveAlert = async (data: AlertEditFormData | Alert) => {
    try {
      if (selectedAlert) {
        // Editing existing alert
        const updatedAlert = data as Alert;
        await updateAlertData(selectedAlert.id, {
          companyId: updatedAlert.company.id,
          destinationEmail: updatedAlert.destinationEmail,
          destinationPhoneNumber: updatedAlert.destinationPhoneNumber,
          isActive: updatedAlert.isActive,
          onlyIfUnreadMessages: updatedAlert.onlyIfUnreadMessages,
          mode: updatedAlert.mode,
          processMessages: updatedAlert.processMessages,
          period: updatedAlert.period,
          interval: updatedAlert.interval,
          startTime: updatedAlert.startTime,
          endTime: updatedAlert.endTime,
          daysOfWeek: updatedAlert.daysOfWeek,
        });
      } else {
        // Adding new alert
        const formData = data as AlertEditFormData;
        const alertData = {
          companyId: formData.companyId,
          destinationEmail:
            formData.deliveryMethod === 'email' || formData.deliveryMethod === 'both'
              ? formData.destinationEmail
              : null,
          destinationPhoneNumber:
            formData.deliveryMethod === 'sms' || formData.deliveryMethod === 'both'
              ? formData.destinationPhoneNumber
              : null,
          isActive: true,
          onlyIfUnreadMessages: formData.onlyIfUnreadMessages,
          mode: formData.mode,
          processMessages: formData.processMessages,
          period: formData.period,
          interval: formData.interval,
          startTime: formData.startTime,
          endTime: formData.endTime,
          daysOfWeek: formData.daysOfWeek,
        };

        await createAlertData(alertData);
      }
      setDrawerOpen(false);
      setSelectedAlert(null);
    } catch (error) {
      console.error('Failed to save alert:', error);
    }
  };

  const handleEditAlert = (alert: Alert) => {
    setSelectedAlert(alert);
    setDrawerOpen(true);
  };

  const handleDeleteAlert = async (alertId: string) => {
    try {
      await deleteAlertData(alertId);

      // Close drawer and clear selected alert if the deleted alert was selected
      if (selectedAlert?.id === alertId) {
        setDrawerOpen(false);
        setSelectedAlert(null);
      }
    } catch (error) {
      console.error('Failed to delete alert:', error);
    }
  };

  // Update just the active status of this specific alert
  const handleToggleActive = useCallback(
    async (clickedAlert: Alert) => {
      try {        
        await updateAlertData(clickedAlert.id, {
          companyId: clickedAlert.company.id,
          destinationEmail: clickedAlert.destinationEmail,
          destinationPhoneNumber: clickedAlert.destinationPhoneNumber,
          isActive: !clickedAlert.isActive,
          onlyIfUnreadMessages: clickedAlert.onlyIfUnreadMessages,
          mode: clickedAlert.mode,
          processMessages: clickedAlert.processMessages,
          period: clickedAlert.period,
          interval: clickedAlert.interval,
          startTime: clickedAlert.startTime,
          endTime: clickedAlert.endTime,
          daysOfWeek: clickedAlert.daysOfWeek,
        });
      } catch (error) {
        console.error('Failed to toggle alert status:', error);
      }
    },
    [updateAlertData],
  );

  const openAddDrawer = () => {
    setSelectedAlert(null);
    setDrawerOpen(true);
  };

  if (isPageLoading) {
    return (
      <Stack direction="row" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography ml={2}>Loading alerts...</Typography>
      </Stack>
    );
  }

  return (
    <>
      <Stack height="75vh" px={3} py={3}>
        {/* Fixed Header Section */}
        <Box flexShrink={0}>
          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            mb={4}
          >
            <Grid item sx={{ maxWidth: '400px', flex: '1 1' }}>
              <TextField
                fullWidth
                placeholder="Search alerts..."
                value={searchQuery}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {isSearching && <CircularProgress size={20} />}
                    </InputAdornment>
                  ),
                }}
                margin="dense"
              />
            </Grid>

            <Grid item sx={{ flex: '1 1' }} />

            <Grid item>
              <Button variant="contained" onClick={openAddDrawer}>
                Add Alert
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Scrollable Content Section with Infinite Scroll */}
        <VisibleScrollbarArea ref={scrollContainerRef} flex={1} minHeight={0}>
          {isPageLoading && !alertsList && (
            <Stack direction="row" justifyContent="center" alignItems="center" minHeight="300px">
              <CircularProgress size={40} />
              <Typography ml={2}>Loading alerts...</Typography>
            </Stack>
          )}
          {(!isPageLoading || alertsList) && alertsToDisplay.length === 0 && (
            <DashedPanel justifyContent="center" alignItems="center" minHeight="300px">
              <Notifications />
              <Typography color="textSecondary" variant="h6" gutterBottom>
                {searchQuery ? 'No alerts match your search' : 'No alerts configured'}
              </Typography>
              <Typography color="textSecondary" variant="body2" mb={3}>
                {searchQuery
                  ? 'Try adjusting your search terms.'
                  : 'Get started by creating your first alert notification.'}
              </Typography>
              {!searchQuery && (
                <Button variant="contained" onClick={openAddDrawer}>
                  Add Your First Alert
                </Button>
              )}
            </DashedPanel>
          )}
          {alertsToDisplay.length > 0 && (
            <Box position="relative">
              <Grid container spacing={3}>
                {alertsToDisplay.map((alert, index) => {
                  console.log(`AlertsView - Rendering AlertCard ${index} with alert:`, {
                    id: alert.id,
                    isActive: alert.isActive,
                    mode: alert.mode,
                    // Include first few chars of other fields to verify identity
                    emailPrefix: alert.destinationEmail?.substring(0, 10),
                    phonePrefix: alert.destinationPhoneNumber?.substring(0, 6),
                  });
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={4} key={`alert-${alert.id}`}>
                      <AlertCard
                        key={`card-${alert.id}`}
                        alert={alert}
                        companyName={alert.company.name}
                        onEdit={handleEditAlert}
                        onDelete={handleDeleteAlert}
                        onToggleActive={handleToggleActive}
                      />
                    </Grid>
                  );
                })}
              </Grid>

              {/* Loading indicator for infinite scroll */}
              {isLoadingMore && (
                <DashedPanel
                  thickness={1}
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                  py={3}
                  mt={2}
                >
                  <CircularProgress size={24} />
                  <Typography ml={2} color="textSecondary">
                    Loading more alerts...
                  </Typography>
                </DashedPanel>
              )}
            </Box>
          )}
        </VisibleScrollbarArea>

        {/* Floating Action Button for mobile */}
        <Fab
          color="primary"
          aria-label="add alert"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            display: { xs: 'flex', sm: 'none' },
          }}
          onClick={openAddDrawer}
        >
          <Add />
        </Fab>

        {/* Alert Drawer (Add/Edit) */}
        <AlertDrawer
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            setSelectedAlert(null);
          }}
          onSave={handleSaveAlert}
          alert={selectedAlert}
          loading={selectedAlert ? isUpdating : isCreating}
        />
      </Stack>
    </>
  );
};
